import type {
  CalendarFilterDateType,
  CheckInOutType,
} from '@vrplatform/components';
import { useDashboard, useOwnerPortalContact, useTeam } from '@vrplatform/data';
import { day, hasValue } from '@vrplatform/utils';
import {
  type ReservationsFilterParams,
  whereReservations,
} from '@vrplatform/ui-common';
import { useMemo } from 'react';
import { useReservationTableFilter } from './useReservationTableFilter';

export function useWhereReservations(opts: {
  filterByAutomationId: string | undefined;
  hideCancellations: boolean;
}) {
  const filter = useFormattedReservationFilter(opts);

  const onlyEnabledListingByAutomationId = opts.filterByAutomationId;

  return useWhereReservationsBase(
    onlyEnabledListingByAutomationId
      ? {
          ...filter,
          onlyEnabledListingByAutomationId,
        }
      : filter
  );
}

export function useFormattedReservationFilter({
  hideCancellations,
}: {
  hideCancellations: boolean;
}) {
  const { filter } = useReservationTableFilter();

  const getDate = (
    date: string | CalendarFilterDateType | undefined | null
  ): ReservationsFilterParams['date'] => {
    if (!date) return undefined;

    if (Array.isArray(date)) {
      const [start, end] = date;

      return [
        start ? day(start).toDate() : null,
        end ? day(end).toDate() : null,
      ];
    }

    if (typeof date !== 'string') return undefined;

    const [start, end] = date.split('...');

    return [start ? day(start).toDate() : null, end ? day(end).toDate() : null];
  };

  return {
    bookingChannels: filter.bookingChannels?.filter(hasValue) || undefined,
    connectionIds: filter.connectionIds?.filter(hasValue) || undefined,
    date: getDate(filter.date),
    dateType: (filter.dateType as CheckInOutType) || 'all',
    listingIds: filter.listingIds?.filter(hasValue) || undefined,
    search: filter.search || undefined,
    listingStatus: (filter.listingStatus as any) || undefined,
    status: hideCancellations ? 'booked' : filter.status || undefined,
    listingCollectionId: filter.listingCollectionId || undefined,
    onlyEnabledListingByAutomationId: undefined,
    paidStatus: (filter.paidStatus ||
      undefined) as ReservationsFilterParams['paidStatus'],
  };
}

export function useWhereReservationsBase(filter: ReservationsFilterParams) {
  const [{ id: teamId, isGL: GL }] = useTeam();
  const [dashboard] = useDashboard();

  const { contactId: ownerPortalContactId } = useOwnerPortalContact();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const where = useMemo(
    () =>
      whereReservations({
        tenantId: teamId,
        dashboard,
        includeLines: false,
        ownerPortalContactId,
        GL,
        status: '',
        ...filter,
      }),
    [JSON.stringify(filter), teamId, dashboard, ownerPortalContactId, GL]
  );

  return where;
}
